import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import {
    Avatar, Chip, IconButton, InputAdornment, MenuItem, Select, TextField, Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import NetworkManager from "../../app/managers/networkManager";
import styles from "./VResume.module.css";
import {
    VRESUME_STATUS_COLORS, exportVResumePdf, listVResumes, openEditor
} from "./vresumeSlice";

// Matches the density of the DataGrids elsewhere in the app, so a user moving between
// screens does not feel the row rhythm change.
const ROW_HEIGHT = 46
const HEADER_HEIGHT = 44

/**
 * V-Resume — the team listing.
 *
 * One row per collaborator whose CV the caller can reach, with how far along it is. Not the
 * CV contents: this is the screen a manager uses to see who still needs to fill theirs in,
 * and the CV itself opens from a row.
 *
 * ── The reach is the server's answer, not a client-side filter ────────────────────────
 *
 * The rows arrive already narrowed — `VResumesController.list` applies the caller's reach in
 * SQL, because the unfiltered version of that query is "every collab in the company". A
 * caller with no `vresume` permission at all receives exactly one row, their own. Per-row
 * `canEdit` also comes from the server, so the button drawn here and the authorisation
 * applied on the call cannot disagree.
 *
 * ── Why search and status filtering are client-side ───────────────────────────────────
 *
 * Unlike V-Reference, which searches full CV text through a FULLTEXT index and must do it
 * server-side, this list is capped at 500 rows of name + status and is already in memory.
 * Filtering it locally is instant and costs no request per keystroke.
 */
export default function VResumeList() {

    const dispatch = useDispatch()

    const list = useSelector((state) => state.vresume.list)
    const reach = useSelector((state) => state.vresume.reach)
    const lang = useSelector((state) => state.settings.lang)
    const userId = useSelector((state) => state.login.userId)

    const [query, setQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState("")

    useEffect(() => {
        dispatch(listVResumes({}))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onExport = useCallback(async (row) => {
        const name = [row.prenom, row.nom].filter(Boolean).join('_')
            .replace(/[^A-Za-z0-9_-]/g, '') || 'CV'
        const response = await dispatch(exportVResumePdf({
            // Own CV: omit the id and let the backend default to the session, the same rule
            // the editor follows.
            idCollab: row.id_collab == userId ? undefined : row.id_collab,
            filename: `CV_${name}.pdf`
        }))
        if (response.payload && response.payload.responseError) {
            toast.error(Localize("VRESUME_EXPORT_FAILED", lang))
        }
    }, [dispatch, lang, userId])

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase()
        return (list || []).filter(row => {
            if (statusFilter && row.status !== statusFilter) return false
            if (!needle) return true
            return [row.nom, row.prenom, row.email, row.headline, row.teams]
                .filter(Boolean)
                .some(field => String(field).toLowerCase().includes(needle))
        })
    }, [list, query, statusFilter])

    const columns = useMemo(() => [
        {
            field: 'person',
            headerName: Localize("VRESUME_COL_COLLAB", lang),
            flex: 1.6,
            minWidth: 220,
            // The row objects have no `person` key, so the grid has nothing to sort or filter
            // on without this. Signature is `(value, row)` — the x-data-grid v8 shape, as
            // used in VReferenceView; the v6 `(params)` form silently yields undefined.
            valueGetter: (value, row) => `${row.nom || ''} ${row.prenom || ''}`.trim(),
            renderCell: (params) => {
                const row = params.row
                const photoUrl = NetworkManager.getInstance()
                    .getCollabPhotoUrl(row.id_collab, row.photo_updated_at)
                const initials = [row.prenom, row.nom].filter(Boolean)
                    .map(part => part.charAt(0).toUpperCase()).join('')
                return (
                    <div className={styles.personCell}>
                        <Avatar src={photoUrl || undefined}
                            sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'var(--lightGrey)', color: '#6f6f6f' }}>
                            {initials}
                        </Avatar>
                        <div style={{ minWidth: 0 }}>
                            <div className={styles.personName}>
                                {[row.prenom, row.nom].filter(Boolean).join(' ')}
                                {row.isSelf
                                    ? <Chip size="small" label={<Langs str="VRESUME_YOU" />}
                                        sx={{
                                            ml: 0.75, height: 16, fontFamily: 'roboto', fontSize: 9.5,
                                            backgroundColor: 'rgba(0, 40, 128, 0.09)', color: 'var(--vital-blue)'
                                        }} />
                                    : null}
                            </div>
                            <div className={styles.personSub}>{row.headline || row.email}</div>
                        </div>
                    </div>
                )
            }
        },
        {
            field: 'teams',
            headerName: Localize("VRESUME_COL_TEAMS", lang),
            flex: 1.2,
            minWidth: 150,
            // `display: block` + `width: 100%`: an inline span in a flex cell shrink-wraps
            // its text, so `text-overflow: ellipsis` never triggers and a long team list
            // overflows instead of truncating.
            renderCell: (params) => (
                <Tooltip title={params.row.teams || ''} arrow>
                    <span className={styles.teamsCell}>{params.row.teams || '—'}</span>
                </Tooltip>
            )
        },
        {
            field: 'completion',
            headerName: Localize("VRESUME_COL_COMPLETION", lang),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const value = params.row.completion || 0
                return (
                    <div className={styles.completionCell}>
                        <div className={styles.completionTrack}>
                            <div
                                className={`${styles.completionFill}`
                                    + (value >= 100 ? ` ${styles.completionFillDone}` : '')}
                                style={{ width: `${value}%` }}
                            />
                        </div>
                        <span className={styles.completionValue}>{value}%</span>
                    </div>
                )
            }
        },
        {
            field: 'status',
            headerName: Localize("VRESUME_COL_STATUS", lang),
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => {
                const status = params.row.status || 'none'
                return (
                    <Chip size="small"
                        label={Localize(`VRESUME_STATUS_${status.toUpperCase()}`, lang)}
                        sx={{
                            ...(VRESUME_STATUS_COLORS[status] || VRESUME_STATUS_COLORS.none),
                            '& .MuiChip-label': { fontFamily: 'roboto', fontSize: 11 }
                        }} />
                )
            }
        },
        {
            field: 'actions',
            headerName: '',
            sortable: false,
            filterable: false,
            width: 100,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => {
                const row = params.row
                return (
                    <div className={styles.rowActions}>
                        <Tooltip title={Localize(row.canEdit
                            ? "VRESUME_OPEN_EDIT" : "VRESUME_OPEN_VIEW", lang)} arrow>
                            <IconButton size="small"
                                onClick={() => dispatch(openEditor(row.id_collab))}>
                                {row.canEdit
                                    ? <EditOutlinedIcon sx={{ fontSize: 18, color: 'var(--vital-blue)' }} />
                                    : <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#6f6f6f' }} />}
                            </IconButton>
                        </Tooltip>
                        {/* A collab who never opened the builder has an empty CV; exporting
                            it would hand over a blank page, so the button is not offered. */}
                        {row.status !== 'none'
                            ? <Tooltip title={Localize("VRESUME_EXPORT", lang)} arrow>
                                <IconButton size="small" onClick={() => onExport(row)}>
                                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: 'var(--orange)' }} />
                                </IconButton>
                            </Tooltip>
                            : null}
                    </div>
                )
            }
        }
    ], [dispatch, lang, onExport])

    /** The line under the title saying how far this user reaches, so the list's contents
     *  are never a mystery. */
    const scopeLabel = () => {
        if (reach.editAll) return "VRESUME_SCOPE_EDIT_ALL"
        if (reach.viewAll) return "VRESUME_SCOPE_VIEW_ALL"
        if (reach.hasTeamReach) return "VRESUME_SCOPE_TEAMS"
        return "VRESUME_SCOPE_SELF"
    }

    return (
        <div>
            <div className={styles.toolbar}>
                <TextField
                    size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={Localize("VRESUME_SEARCH_PLACEHOLDER", lang)}
                    sx={{ flex: '0 1 320px' }}
                    InputProps={{
                        sx: { fontFamily: 'roboto', fontSize: 13 },
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: '#a0a5b1' }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Select
                    size="small"
                    value={statusFilter}
                    displayEmpty
                    onChange={(event) => setStatusFilter(event.target.value)}
                    sx={{ flex: '0 1 190px', fontFamily: 'roboto', fontSize: 13 }}>
                    <MenuItem value="" sx={{ fontFamily: 'roboto', fontSize: 13 }}>
                        <Langs str="VRESUME_STATUS_ALL" />
                    </MenuItem>
                    {['none', 'draft', 'complete'].map(status => (
                        <MenuItem key={status} value={status} sx={{ fontFamily: 'roboto', fontSize: 13 }}>
                            {Localize(`VRESUME_STATUS_${status.toUpperCase()}`, lang)}
                        </MenuItem>
                    ))}
                </Select>

                <div className={styles.toolbarSpacer} />

                <span className={styles.headerScope}><Langs str={scopeLabel()} /></span>
            </div>

            <div className={styles.gridWrapper}>
                <DataGrid
                    rows={filtered}
                    columns={columns}
                    getRowId={(row) => row.id_collab}
                    rowHeight={ROW_HEIGHT}
                    columnHeaderHeight={HEADER_HEIGHT}
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                    localeText={{
                        noRowsLabel: Localize("VRESUME_NO_ROWS", lang)
                    }}
                    sx={{
                        border: 'none',
                        fontFamily: 'roboto',
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontSize: 12, fontWeight: 700, color: '#5a6070'
                        },
                        /*
                         * The cell has to be made a flex container, and its line-height has
                         * to be reset. Both matter, for the same underlying reason.
                         *
                         * x-data-grid v8 does NOT make the cell a flex container. It centres
                         * plain text vertically with `line-height: calc(var(--height) - 1px)`
                         * — about 45px on a 46px row — which works beautifully for a bare
                         * string and breaks anything else:
                         *
                         *   1. A `renderCell` wrapper is a block child, so line-height does
                         *      not centre it; it sits at the TOP of the cell while the text
                         *      in neighbouring plain cells sits in the middle. That is the
                         *      avatars-not-lining-up-with-the-rows symptom.
                         *   2. Worse, that ~45px line-height is INHERITED into the wrapper's
                         *      own text. The two-line name/subtitle block became ~90px tall
                         *      inside a 46px row and was clipped by the cell's
                         *      `overflow: hidden`.
                         *
                         * `align`/`headerAlign` also depend on this: v8's
                         * `.MuiDataGrid-cell--textRight` sets `justify-content: flex-end`,
                         * which is inert until the cell is actually flex — so the actions
                         * column was not right-aligning either.
                         *
                         * Safe to apply to every cell here because all five columns render
                         * through `renderCell`; no cell relies on the grid's own
                         * text-overflow ellipsis, which a flex container would defeat.
                         */
                        '& .MuiDataGrid-cell': {
                            fontSize: 12.5,
                            borderColor: '#f1f2f6',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 'normal'
                        },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#fafbfd' },
                        '& .MuiDataGrid-footerContainer': { borderColor: '#f1f2f6' }
                    }}
                    autoHeight
                />
            </div>
        </div>
    )
}
